import Stats from 'stats.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader.js'
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js'
import { fishshader } from '@/shaders/fishshader'
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js'
import SceneSizeService from '@/services/Three/scene-size.service'
import * as THREE from 'three'
import { OrbitControls } from '@/utils/OrbitControls'
import SentrySingleton from '@/singletons/sentry-singleton'

export default class SceneService {
    private sceneSizeService: SceneSizeService

    constructor() {
        this.sceneSizeService = new SceneSizeService()
    }

    onWindowResize() {
        this.sceneSizeService.onWindowResize(this.sceneSizeService)
    }

    createScene(): THREE.Scene {
        return new THREE.Scene()
    }

    createRenderer(canvas: HTMLCanvasElement, sceneContainer: HTMLCanvasElement, postprocessing: boolean, alpha: boolean): THREE.WebGLRenderer {
        this.sceneSizeService.updateContainerSize(sceneContainer, canvas)
        const renderer = new THREE.WebGLRenderer({
            antialias: !postprocessing,
            canvas: canvas,
            alpha: alpha
        })
        renderer.setSize(sceneContainer.clientWidth, sceneContainer.clientHeight)
        renderer.setPixelRatio(window.devicePixelRatio)
        renderer.setClearColor(0xffffff, alpha ? 0 : 1)
        renderer.autoClear = false
        renderer.outputEncoding = THREE.sRGBEncoding
        // renderer.sRGBOutput = true;
        renderer.gammaFactor = 2.2
        renderer.physicallyCorrectLights = true
        sceneContainer.appendChild(renderer.domElement)
        this.sceneSizeService.updateCanvasSize(sceneContainer, canvas)
        return renderer
    }

    createCamera(sceneContainer: HTMLCanvasElement): THREE.PerspectiveCamera {
        const camera = new THREE.PerspectiveCamera(30, sceneContainer.clientWidth / sceneContainer.clientHeight, 0.1, 1000)
        camera.position.copy(new THREE.Vector3(0, 100, 0))
        return camera
    }

    createControls(camera: THREE.PerspectiveCamera, sceneContainer: HTMLCanvasElement, isControlsEnabled: boolean): OrbitControls {
        const controls = new OrbitControls(camera, sceneContainer)
        controls.enablePan = false
        controls.target = new THREE.Vector3(0, 0, 0)
        controls.enableDamping = true
        controls.dampingFactor = 0.05
        controls.rotateSpeed = 1
        controls.enabled = isControlsEnabled
        return controls
    }

    createLights(scene: THREE.Scene) {
        try {
            // const isFull = RandselLocalStorage.isFullorder();
            const isFull = true
            RectAreaLightUniformsLib.init()

            const ambientLight = new THREE.HemisphereLight(0xffffff, 0x888888, 4)
            scene.add(ambientLight)

            var width = 15
            var height = isFull ? 0.25 : 0.15
            let defaultRectLightIntensity = isFull ? 3 : 12
            const rectLight = new THREE.RectAreaLight(0xffffff, defaultRectLightIntensity, width, height)
            rectLight.position.set(0, 0.3, -0.4)
            rectLight.lookAt(0, -0.5, 0)
            scene.add(rectLight)
            // rectLight.add(new RectAreaLightHelper(rectLight));

            var rightLight = new THREE.PointLight(0x333333, 4)
            rightLight.position.set(-1.5, 0, 0)
            scene.add(rightLight)
            // scene.add(new THREE.PointLightHelper(rightLight, 0.1, 0x00ff00));

            var leftLight = new THREE.PointLight(0x333333, 4)
            leftLight.position.set(1.5, 0, -0)
            scene.add(leftLight)
            // scene.add(new THREE.PointLightHelper(leftLight, 0.1, 0x00ff00));
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
        }
    }

    createComposer(scene: THREE.Scene, camera: THREE.Camera, renderer: THREE.WebGLRenderer, postprocessing: boolean): EffectComposer | null {
        try {
            if (!postprocessing) {
                return null
            }
            let composer = new EffectComposer(renderer)
            composer.addPass(new RenderPass(scene, camera))
            return composer
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
            return null
        }
    }

    createFxaaPass(composer: EffectComposer, sceneContainer: HTMLCanvasElement): ShaderPass {
        try {
            let fxaaPass = new ShaderPass(FXAAShader)
            const fxaaWidth = sceneContainer.clientWidth * window.devicePixelRatio
            const fxaaHeight = sceneContainer.clientWidth * window.devicePixelRatio // WTF clientWidth?
            fxaaPass.uniforms['resolution'].value.set(1 / fxaaWidth, 1 / fxaaHeight)
            composer.addPass(fxaaPass)
            return fxaaPass
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
            return new ShaderPass(FXAAShader)
        }
    }

    createFisheyePass(composer: EffectComposer, camera: THREE.PerspectiveCamera): ShaderPass {
        try {
            var fisheyePass = new ShaderPass(fishshader)
            fisheyePass.renderToScreen = true
            composer.addPass(fisheyePass)

            var horizontalFOV = 30
            var strength = 0.1
            var cylindricalRatio = 2
            var height = Math.tan(THREE.MathUtils.degToRad(horizontalFOV) / 2) / camera.aspect
            camera.fov = (Math.atan(height) * 2 * 180) / 3.1415926535
            camera.updateProjectionMatrix()

            fisheyePass.uniforms['strength'].value = strength
            fisheyePass.uniforms['height'].value = height
            fisheyePass.uniforms['aspectRatio'].value = camera.aspect
            fisheyePass.uniforms['cylindricalRatio'].value = cylindricalRatio
            return fisheyePass
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
            return new ShaderPass(fishshader)
        }
    }

    createGammaCorrectionPass(composer: EffectComposer, postprocessing: boolean): ShaderPass | null {
        try {
            if (!postprocessing) {
                return null
            }
            let gammaCorrectionPass = new ShaderPass(GammaCorrectionShader)
            composer.addPass(gammaCorrectionPass)
            return gammaCorrectionPass
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
            return null
        }
    }

    createFPS() {
        let stats = new Stats()
        stats.setMode(0)
        stats.domElement.style.position = 'absolute'
        stats.domElement.style.left = '10px'
        stats.domElement.style.top = '10px'
        document.querySelector('#scene-container')!.appendChild(stats.domElement)
        return stats
    }

    cleanScene(scene: THREE.Scene) {
        try {
            scene.traverse((object: any) => {
                if (!object.isMesh) {
                    return
                }

                object.geometry.dispose()
                if (object.material.isMaterial) {
                    this.cleanMaterial(object.material)
                } else {
                    for (const material of object.material) {
                        this.cleanMaterial(material)
                    }
                }
            })
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
        }
    }

    private cleanMaterial(material: any) {
        material.dispose()
        for (const key of Object.keys(material)) {
            const value = material[key]
            if (value && typeof value === 'object' && 'minFilter' in value) {
                value.dispose()
            }
        }
    }
}